import P from '@hero/ui-kit/typography/P';
import React from 'react';
import Layout from '../../../../components/Layout';
import leadSearchProps from '../../leadSearchProps';
import * as Style from './style.module.scss';
import getApprovalStatus from '../../utils/getApprovalStatus';
import getLeadName from '../../utils/getLeadName';
import { useParams } from 'react-router-dom';
import useGetLeadState from '../../api/useGetLeadState';
import useGetLeadDetails from '../../api/useGetLeadDetails';
import { ApprovalStatus } from '../../types';
import Loading from '../../../../components/States/Loading';
import AlertIcon from '@hero/ui-kit/icons/utility/AlertIcon';
import { useUserStatusContext } from '../../../../context/UserStatus';
import useExtendLockUnlockLead from '../../hooks/useExtendLockUnlockLead';
import LeadActivities from '../LeadActivities';
import HealthAssessment from '../HealthAssessment';
import Section from '@hero/ui-kit/layout/Section';
import General from './components/General';
import CalendarIcon from '@hero/ui-kit/icons/utility/CalendarIcon';
import Pill from '../../components/Pill';
import VerifiedIcon from '@hero/ui-kit/icons/utility/VerifiedIcon';
import LeadContactDetails from '../LeadContactDetails';
import StickyNotePanel from '../StickyNotePanel';
import LeadDetailsProvider, { useLeadDetailsContext } from './context/LeadDetailsContext';
import ListNav from '../../../../components/ListNav';
import useFormFillsListNeighbors from './useFormFillsListNeighbors';
import getShouldShowExpiringAlert from '../../utils/getShouldShowExpiringAlert';
import ExpiringAlert from '../../components/ExpiringAlert';
import { getFormattedDate, parseToDate } from '../../../../utils/date';

const getLabel = (label?: string | null) => {
    return label || '-';
};

const DetailsExpiringAlert: React.FC<{ days?: number; hours?: number; show?: boolean }> = ({
    days,
    hours,
    show
}) => {
    const daysText = days && days > 0 ? `${days} days!` : `${hours} hours!`;

    return show ? (
        <div style={{ display: 'flex', alignItems: 'center', columnGap: '1rem' }}>
            <AlertIcon size="utility" width={20} />

            <P noDefaultMargin strong>{`Application expires in ${daysText}`}</P>
        </div>
    ) : null;
};

const LeadDetailsV2Inner: React.FC<{ id: string }> = ({ id }) => {
    const { isFormChanged, tab, handleChangeTab, handlePrepareChangeTab } = useLeadDetailsContext();

    const { prev, next, backTo, position } = useFormFillsListNeighbors();

    const { userStatus, permissions } = useUserStatusContext();
    const {
        data: lead,
        refetch: refetchLeadDetails,
        isLoading: isGetLeadDetailsLoading
    } = useGetLeadDetails(id);

    const {
        data: leadState,
        refetch: refetchLeadState,
        isLoading: isGetLeadStateLoading
    } = useGetLeadState(id);

    const isLocked = !!lead?.claimed_by_id && lead?.claimed_by_id !== userStatus?.hero_user_id;

    useExtendLockUnlockLead(
        lead?.lock_expires || undefined,
        id,
        lead?.claimed_by_id === userStatus?.hero_user_id,
        permissions.leads.leadListEdit
    );

    const canEdit = isLocked ? false : permissions.leads.leadListEdit;

    const showAlert = getShouldShowExpiringAlert({
        isLocked,
        approval_status: lead?.status,
        approval_sub_status: lead?.sub_status
    });

    const approvalStatus = getApprovalStatus(lead?.status.toUpperCase() as ApprovalStatus);

    const tabs = [
        {
            label: 'Activities',
            value: 'activities',
            show: true
        },
        {
            label: 'Demographics',
            value: 'general',
            show: true
        },
        {
            label: 'Health Assessment',
            value: 'health_assessment',
            show: true
        }
    ];

    const handleSetTab = (tabItemValue: string) => {
        if (!isFormChanged) {
            handleChangeTab(tabItemValue);
        } else {
            handlePrepareChangeTab(tabItemValue);
        }
    };

    const createdAtDate =
        lead?.primary_user_details?.completed_date || lead?.primary_user_details?.created_date;

    return (
        <>
            {isLocked ? (
                <div className={Style.lockedAlertWrapper}>
                    <div className={Style.lockedAlert}>
                        <AlertIcon type="utility" width={22} height={20} />
                        <P noDefaultMargin strong>
                            Another agent is working with the patient currently
                        </P>
                    </div>
                </div>
            ) : null}
            {/* `searchTo` makes the header search a jump-to control: selecting a
                suggestion routes straight to that form fill, and submitting without a
                highlighted suggestion hands the term to the form-fills list instead of
                filtering this page. */}
            <Layout paddingHorizontal="none" {...leadSearchProps} searchTo="/form-fills">
                {isGetLeadDetailsLoading || isGetLeadStateLoading ? (
                    <Loading />
                ) : (
                    <Section
                        styles={{
                            width: '100%'
                        }}
                        paddingTop="none"
                        paddingBottom="none"
                        paddingHorizontal="none"
                    >
                        <Section noDefaultPadding paddingHorizontal="regular">
                            <ListNav
                                backLabel="Form fills"
                                backAlt="Back to form fills"
                                backTo={backTo}
                                prev={prev}
                                next={next}
                                position={position}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: '3.6rem',
                                        marginTop: '2.4rem',
                                        marginBottom: '1.2rem'
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            columnGap: '1.2rem'
                                        }}
                                    >
                                        {lead?.is_existing_member ? (
                                            <VerifiedIcon type="utility" width={36} height={36} />
                                        ) : null}
                                        <P
                                            strong
                                            noDefaultMargin
                                            styles={{ fontSize: '3.2rem', lineHeight: '4rem' }}
                                        >
                                            {getLeadName(lead)}
                                        </P>
                                    </div>

                                    <Pill
                                        label={approvalStatus.label}
                                        className={approvalStatus.className}
                                    />
                                </div>
                                {showAlert && lead?.primary_user_details.created_date ? (
                                    <ExpiringAlert expiresAt={lead.primary_user_details.expires_at}>
                                        <DetailsExpiringAlert />
                                    </ExpiringAlert>
                                ) : null}
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    columnGap: '1rem',
                                    marginBottom: '2.4rem'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: '1rem'
                                    }}
                                >
                                    <CalendarIcon type="utility" width={20} height={20} />
                                    <P noDefaultMargin size="small">
                                        {createdAtDate
                                            ? getFormattedDate(
                                                  parseToDate(createdAtDate),
                                                  'MMM dd, yyyy HH:mm:ss'
                                              )
                                            : '-'}
                                    </P>
                                </div>
                                <P
                                    noDefaultMargin
                                    styles={{
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden'
                                    }}
                                    title={getLabel(lead?.primary_user_details?.lead_id)}
                                >
                                    <strong>Form fill ID:</strong>{' '}
                                    <code>{getLabel(lead?.primary_user_details?.lead_id)}</code>
                                </P>
                            </div>

                            <div className={Style.nav}>
                                {tabs
                                    .filter((tabItem) => tabItem.show)
                                    .map((tabItem) => (
                                        <button
                                            className={tabItem.value === tab ? Style.active : ''}
                                            key={tabItem.label}
                                            onClick={() => handleSetTab(tabItem.value)}
                                        >
                                            {tabItem.label}
                                        </button>
                                    ))}
                            </div>
                        </Section>
                        {tab === 'general' ? <General lead={lead} canEdit={canEdit} /> : null}
                        {tab === 'activities' ? (
                            <div className={Style.activitiesColumns}>
                                <div className={Style.activitiesLeftColumn}>
                                    <StickyNotePanel leadId={id} lead={lead} canEdit={canEdit} />
                                    <LeadContactDetails
                                        hideNameSection
                                        approvalStatus={approvalStatus}
                                        lead={lead}
                                        refetchLeadDetails={refetchLeadDetails}
                                        refetchLeadState={refetchLeadState}
                                        isLocked={isLocked}
                                        hideEdit
                                        showCrmActions
                                        leadState={leadState}
                                        leadId={id}
                                        canEdit={canEdit}
                                    />
                                </div>
                                <Section
                                    border
                                    styles={{
                                        width: '100%'
                                    }}
                                    paddingTop="small"
                                    paddingBottom="none"
                                    paddingHorizontal="regular"
                                    borderBottom={false}
                                >
                                    <LeadActivities
                                        leadId={id}
                                        refetchLeadDetails={refetchLeadDetails}
                                        refetchLeadState={refetchLeadState}
                                        lead={lead}
                                        leadState={leadState}
                                        isLocked={isLocked}
                                        canEdit={permissions.leads.leadListEdit}
                                    />
                                </Section>
                            </div>
                        ) : null}
                        {tab === 'health_assessment' ? (
                            <div style={{ padding: '3.6rem 3.6rem' }}>
                                <HealthAssessment lead={lead} />
                            </div>
                        ) : null}
                    </Section>
                )}
            </Layout>
        </>
    );
};

const LeadDetailsV2: React.FC = () => {
    const { id = '' } = useParams<Required<{ id: string }>>();

    return (
        <LeadDetailsProvider>
            <LeadDetailsV2Inner id={id} />
        </LeadDetailsProvider>
    );
};

export default LeadDetailsV2;
